import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, Grid, Chip, Button, CircularProgress, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Select, FormControl, InputLabel, Snackbar, Alert, IconButton, Paper
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BusinessIcon from '@mui/icons-material/Business';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AssignmentIcon from '@mui/icons-material/Assignment';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ApiService from '../../../../services/api';
import logger from '../../../../utils/logger';
import { useAdminRole } from '../../../../contexts/AdminRoleContext';

const Container = styled(Box)(({ theme }) => ({
  padding: '24px',
  backgroundColor: '#f5f7fa',
  minHeight: '100vh',
}));

const TitleSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '24px',
}));

const TitleIcon = styled(Box)(({ theme }) => ({
  width: '48px',
  height: '48px',
  borderRadius: '12px',
  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  boxShadow: '0 4px 14px rgba(16, 185, 129, 0.4)',
}));

const ProfileCard = styled(Card)(({ theme }) => ({
  borderRadius: '20px',
  padding: '24px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
  marginBottom: '24px',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '16px',
  fontWeight: 700,
  color: '#10b981',
  marginBottom: '16px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
}));

const InfoRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '12px',
  marginBottom: '12px',
  '& .label': {
    color: '#6b7280',
    fontSize: '13px',
    minWidth: '120px',
  },
  '& .value': {
    color: '#1f2937',
    fontWeight: 500,
    fontSize: '14px',
  },
}));

const StatusChip = styled(Chip)(({ status }) => {
  const colors = {
    pending: { bg: '#fef3c7', color: '#d97706' },
    in_progress: { bg: '#dbeafe', color: '#2563eb' },
    completed: { bg: '#d1fae5', color: '#059669' },
    cancelled: { bg: '#fee2e2', color: '#dc2626' },
  };
  const c = colors[status] || colors.pending;
  return { backgroundColor: c.bg, color: c.color, fontWeight: 600 };
});

const PriorityChip = styled(Chip)(({ priority }) => {
  const colors = {
    high: { bg: '#fee2e2', color: '#dc2626' },
    normal: { bg: '#e0e7ff', color: '#4f46e5' },
    low: { bg: '#f3f4f6', color: '#6b7280' },
  };
  const c = colors[priority] || colors.normal;
  return { backgroundColor: c.bg, color: c.color, fontWeight: 600 };
});

const ActionButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  borderRadius: '10px',
  fontWeight: 600,
  padding: '10px 20px',
}));

const FollowupProfile = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { getRoleFilter } = useAdminRole();
  const roleFilter = getRoleFilter();
  const [followup, setFollowup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completeDialog, setCompleteDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [completeForm, setCompleteForm] = useState({ outcome: '', outcome_notes: '', call_duration: '' });

  // Inside sales team check (BDM Sales inside, CRO) - legacy 'telly'
  const isInsideSales = roleFilter === 'bdm_inside' || roleFilter === 'cro' || roleFilter === 'telly';
  // Outside sales team check (BDM Sales outside) - legacy 'bdm'
  const isOutsideSales = roleFilter === 'bdm_outside' || roleFilter === 'bdm' || roleFilter === 'cro';

  useEffect(() => {
    fetchFollowup();
  }, [id]);

  const fetchFollowup = async () => {
    try {
      setLoading(true);
      const response = await ApiService.get(`/admin/followups/${id}`);
      const data = response?.data?.data || response?.data || response;
      setFollowup(data);
    } catch (error) {
      logger.error('Error fetching followup:', error);
      setSnackbar({ open: true, message: 'Failed to load follow-up details', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    try {
      await ApiService.put(`/admin/followups/${id}/complete`, completeForm);
      setCompleteDialog(false);
      setSnackbar({ open: true, message: 'Follow-up completed successfully!', severity: 'success' });
      fetchFollowup();
    } catch (error) {
      logger.error('Error completing followup:', error);
      setSnackbar({ open: true, message: 'Failed to complete follow-up', severity: 'error' });
    }
  };

  const handleDelete = async () => {
    try {
      await ApiService.delete(`/admin/followups/${id}`);
      setSnackbar({ open: true, message: 'Follow-up deleted', severity: 'success' });
      setTimeout(() => navigate('/admin-dashboard/followups'), 1000);
    } catch (error) {
      logger.error('Error deleting followup:', error);
      setSnackbar({ open: true, message: 'Failed to delete follow-up', severity: 'error' });
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatDateTime = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress sx={{ color: '#10b981' }} />
        </Box>
      </Container>
    );
  }

  if (!followup) {
    return (
      <Container>
        <Box textAlign="center" py={8}>
          <Typography variant="h6" color="text.secondary">Follow-up not found</Typography>
          <Button variant="contained" onClick={() => navigate('/admin-dashboard/followups')} sx={{ mt: 2 }}>
            Back to Follow-ups
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container>
      <TitleSection>
        <IconButton onClick={() => navigate('/admin-dashboard/followups')} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <TitleIcon>
          <AssignmentIcon sx={{ fontSize: 24 }} />
        </TitleIcon>
        <Box flex={1}>
          <Typography variant="h4" fontWeight={700} color="#1f2937">{followup.title}</Typography>
          <Typography variant="body2" color="text.secondary">
            Created {formatDateTime(followup.created_at)}
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <StatusChip status={followup.status} label={followup.status?.replace('_', ' ')} />
          <PriorityChip priority={followup.priority} label={followup.priority} />
        </Box>
      </TitleSection>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <ProfileCard>
            <SectionTitle>
              <AssignmentIcon fontSize="small" /> Follow-up Details
            </SectionTitle>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <InfoRow>
                  <Typography className="label">Type:</Typography>
                  <Typography className="value">{followup.followup_type || 'Call'}</Typography>
                </InfoRow>
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoRow>
                  <Typography className="label">Priority:</Typography>
                  <PriorityChip priority={followup.priority} label={followup.priority} size="small" />
                </InfoRow>
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoRow>
                  <Typography className="label">Scheduled Date:</Typography>
                  <Typography className="value">{formatDate(followup.scheduled_date)}</Typography>
                </InfoRow>
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoRow>
                  <Typography className="label">Scheduled Time:</Typography>
                  <Typography className="value">{followup.scheduled_time || 'N/A'}</Typography>
                </InfoRow>
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoRow>
                  <Typography className="label">Due Date:</Typography>
                  <Typography className="value">{formatDate(followup.due_date)}</Typography>
                </InfoRow>
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoRow>
                  <Typography className="label">Status:</Typography>
                  <StatusChip status={followup.status} label={followup.status?.replace('_', ' ')} size="small" />
                </InfoRow>
              </Grid>
              {followup.description && (
                <Grid item xs={12}>
                  <InfoRow>
                    <Typography className="label">Description:</Typography>
                    <Typography className="value">{followup.description}</Typography>
                  </InfoRow>
                </Grid>
              )}
            </Grid>

            {followup.status === 'completed' && (
              <>
                <Divider sx={{ my: 3 }} />
                <SectionTitle>
                  <CheckCircleIcon fontSize="small" /> Completion Details
                </SectionTitle>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <InfoRow>
                      <Typography className="label">Outcome:</Typography>
                      <Typography className="value">{followup.outcome || 'N/A'}</Typography>
                    </InfoRow>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InfoRow>
                      <Typography className="label">Call Duration:</Typography>
                      <Typography className="value">{followup.call_duration ? `${followup.call_duration} min` : 'N/A'}</Typography>
                    </InfoRow>
                  </Grid>
                  <Grid item xs={12}>
                    <InfoRow>
                      <Typography className="label">Notes:</Typography>
                      <Typography className="value">{followup.outcome_notes || 'N/A'}</Typography>
                    </InfoRow>
                  </Grid>
                  <Grid item xs={12}>
                    <InfoRow>
                      <Typography className="label">Completed At:</Typography>
                      <Typography className="value">{formatDateTime(followup.completed_at)}</Typography>
                    </InfoRow>
                  </Grid>
                </Grid>
              </>
            )}
          </ProfileCard>

          {followup.lead && (
            <ProfileCard>
              <SectionTitle>
                <BusinessIcon fontSize="small" /> Lead/Client Information
              </SectionTitle>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <InfoRow>
                    <PersonIcon sx={{ color: '#6b7280', fontSize: 20 }} />
                    <Box>
                      <Typography className="label">Business Name</Typography>
                      <Typography className="value">{followup.lead.business_name || 'N/A'}</Typography>
                    </Box>
                  </InfoRow>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoRow>
                    <PersonIcon sx={{ color: '#6b7280', fontSize: 20 }} />
                    <Box>
                      <Typography className="label">Contact Person</Typography>
                      <Typography className="value">{followup.lead.contact_person || 'N/A'}</Typography>
                    </Box>
                  </InfoRow>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoRow>
                    <PhoneIcon sx={{ color: '#6b7280', fontSize: 20 }} />
                    <Box>
                      <Typography className="label">Phone</Typography>
                      <Typography className="value">{followup.lead.phone_number || 'N/A'}</Typography>
                    </Box>
                  </InfoRow>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoRow>
                    <EmailIcon sx={{ color: '#6b7280', fontSize: 20 }} />
                    <Box>
                      <Typography className="label">Email</Typography>
                      <Typography className="value">{followup.lead.email || 'N/A'}</Typography>
                    </Box>
                  </InfoRow>
                </Grid>
              </Grid>

              <Box mt={2} display="flex" gap={1}>
                {followup.lead.phone_number && (
                  <>
                    <ActionButton
                      variant="outlined"
                      color="primary"
                      startIcon={<PhoneIcon />}
                      component="a"
                      href={`tel:${followup.lead.phone_number}`}
                    >
                      Call
                    </ActionButton>
                    <ActionButton
                      variant="outlined"
                      color="success"
                      startIcon={<WhatsAppIcon />}
                      component="a"
                      href={`https://wa.me/${followup.lead.phone_number.replace(/\D/g, '')}`}
                      target="_blank"
                    >
                      WhatsApp
                    </ActionButton>
                  </>
                )}
                <ActionButton
                  variant="outlined"
                  onClick={() => navigate(`/admin-dashboard/lead/${followup.lead.id}`)}
                >
                  View Full Profile
                </ActionButton>
              </Box>
            </ProfileCard>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          <ProfileCard>
            <SectionTitle>
              <PersonIcon fontSize="small" /> Assignment
            </SectionTitle>

            <Paper elevation={0} sx={{ p: 2, bgcolor: '#f0fdf4', borderRadius: 2, mb: 2 }}>
              <Typography variant="caption" color="text.secondary">Assigned To</Typography>
              <Typography fontWeight={600}>
                {followup.assignedEmployee ? `${followup.assignedEmployee.first_name} ${followup.assignedEmployee.last_name || ''}` : 'N/A'}
              </Typography>
              {followup.assignedEmployee?.email && (
                <Typography variant="body2" color="text.secondary">{followup.assignedEmployee.email}</Typography>
              )}
            </Paper>

            <Paper elevation={0} sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary">Assigned By</Typography>
              <Typography fontWeight={600}>
                {followup.assigner?.full_name || 'N/A'}
              </Typography>
              {followup.assigner?.email && (
                <Typography variant="body2" color="text.secondary">{followup.assigner.email}</Typography>
              )}
            </Paper>
          </ProfileCard>

          <ProfileCard>
            <SectionTitle>
              <ScheduleIcon fontSize="small" /> Actions
            </SectionTitle>

            <Box display="flex" flexDirection="column" gap={2}>
              {followup.status !== 'completed' && (
                <ActionButton
                  variant="contained"
                  color="success"
                  fullWidth
                  startIcon={<CheckCircleIcon />}
                  onClick={() => setCompleteDialog(true)}
                >
                  Mark as Complete
                </ActionButton>
              )}

              {isOutsideSales && (
                <ActionButton
                  variant="outlined"
                  color="error"
                  fullWidth
                  startIcon={<DeleteIcon />}
                  onClick={() => setDeleteDialog(true)}
                >
                  Delete Follow-up
                </ActionButton>
              )}
            </Box>
          </ProfileCard>
        </Grid>
      </Grid>

      {/* Complete Dialog */}
      <Dialog open={completeDialog} onClose={() => setCompleteDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Complete Follow-up</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Outcome</InputLabel>
              <Select
                value={completeForm.outcome}
                onChange={(e) => setCompleteForm({ ...completeForm, outcome: e.target.value })}
                label="Outcome"
              >
                <MenuItem value="connected">Connected - Successful</MenuItem>
                <MenuItem value="no_answer">No Answer</MenuItem>
                <MenuItem value="busy">Busy - Call Back Later</MenuItem>
                <MenuItem value="wrong_number">Wrong Number</MenuItem>
                <MenuItem value="not_interested">Not Interested</MenuItem>
                <MenuItem value="follow_up_required">Follow-up Required</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Call Duration (minutes)"
              type="number"
              value={completeForm.call_duration}
              onChange={(e) => setCompleteForm({ ...completeForm, call_duration: e.target.value })}
            />
            <TextField
              label="Notes"
              multiline
              rows={3}
              value={completeForm.outcome_notes}
              onChange={(e) => setCompleteForm({ ...completeForm, outcome_notes: e.target.value })}
              placeholder="Add any notes about this follow-up..."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCompleteDialog(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleComplete}>Mark Complete</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Delete Follow-up?</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this follow-up? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Container>
  );
};

export default FollowupProfile;
